/* global BigInt */
import { createContext, useState, useEffect, useMemo } from 'react';
import { useInventory } from '../hooks';
import { BASEJUMP_CONTRACT_ADDRESS } from '../constants';
import BasejumpContractABI from '../contract.abi'
import { parseEther } from 'viem';
import { useReadContracts, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { useDebouncedCallback } from 'use-debounce';
import { updateTokenEquipment } from './EquipmentProvider';
import { useDisconnect, useConfig } from 'wagmi';
import { signMessage } from '@wagmi/core';
import { Network } from 'alchemy-sdk';

const IS_PRODUCTION = (process.env.REACT_APP_IS_PRODUCTION || 'false').toLowerCase() === 'true'

export const AppContext = createContext({});

const EQUIPMENT_UPDATE_DEBOUCE = 1000;

export const AppContextProvider = ({
  children,
}) => {
  const [showSelector, setShowSelector] = useState(false);
  const [selectedBroadsider, setSelectedBroadsider] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [transferAsset, setTransferAsset] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [errorData, setErrorData] = useState(null);
  const [showDesktopOnly, setShowDesktopOnly] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [ownedBroadsiders, setOwnedBroadsiders] = useState([]);
  const [metaverseItems, setMetaverseItems] = useState([]);
  const [promoItems, setPromoItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [voiceoverApiKey, setVoiceoverApiKey] = useState('');
  const [voices, setVoices] = useState([]);
  const [inventoryUpdated, setInventoryUpdated] = useState('');
  const [moonpayProvider, setMoonpayProvider] = useState(null);
  const [moonpaySigner, setMoonpaySigner] = useState(null);
  const [moonpaySdk, setMoonpaySdk] = useState(null);
  const [accountAddress, setAccountAddress] = useState(null);
  const [walletAddress, _setWalletAddress] = useState(null);
  const [showMintModal, setShowMintModal] = useState(false);
  const [mapSrc, setMapSrc] = useState(null);
  const [isMintingInProgress, setMintingInProgress] = useState(false);
  const [mintFigures, setMintFigures]  = useState(1);
  const [priceWei, setPriceWei] = useState(0n);
  const [completeReceipt, setCompleteReceipt] = useState(null);
  const [equipmentSignature, setEquipmentSignature] = useState(null);
  const { disconnect } = useDisconnect();
  const [showUpload, setShowUpload] = useState(false);

  const setWalletAddress = async (address) => {
    setEquipmentSignature(null);
    try {
      if (address && !equipmentSignature) {
        const signature = await signMessage(wagmiConfig, { message: address });
        setEquipmentSignature(signature);
      }
      _setWalletAddress(address);
    } catch (e) {
      disconnect();
      setErrorData({ title: 'Error', description: 'Failed to connect wallet!' });
    }
  };

  const inventory = useInventory(inventoryItems);
  const { data: readData, isLoading: isReadLoading, refetch: reloadContract } = useReadContracts({
    contracts: [
      { address: BASEJUMP_CONTRACT_ADDRESS, abi: BasejumpContractABI, functionName: 'tokenPrice' },
      { address: BASEJUMP_CONTRACT_ADDRESS, abi: BasejumpContractABI, functionName: 'totalSupply' }
    ]
  });
  const { data: hash, error, writeContract, isPending } = useWriteContract();
  const { data: receipt, isLoading: isConfirming, error: transactionError, isSuccess: isConfirmed } = useWaitForTransactionReceipt({ hash });
  const wagmiConfig = useConfig();

  useEffect(() => {
    if (error) {
      setErrorData({ title: 'Error', description: error.shortMessage });
    }
  }, [error])

  useEffect(() => {
    setMintingInProgress(isPending || isConfirming);
  }, [isPending, isConfirming]);

  useEffect(() => {
    if (transactionError) {
      setErrorData({ title: 'Error', description: transactionError.shortMessage });
    }
  }, [transactionError])

  useEffect(() => {
    if (receipt) {
      setCompleteReceipt(receipt);
      reloadContract();
    }
  }, [receipt])

  useEffect(() => {
    if (readData) {
      setPriceWei(readData[0].result);
    }
    
  }, [readData, isReadLoading]);  

  const updateEquipment = useDebouncedCallback(async (figureTokenId, itemTokenIds, signature, message, network) => {
    try {
      await updateTokenEquipment(figureTokenId, itemTokenIds, signature, message, network);
    } catch (error) {
      setErrorData({ title: 'Error', description: 'Failed to update equipment!' });
    }
  }, EQUIPMENT_UPDATE_DEBOUCE);

  const selectBroadsider = (figure) => {
    if (figure.getId() !== selectedBroadsider.getId()) {
      setInventoryItems([]);
      setSelectedBroadsider(figure);
    }
  };
  
  const equipItem = async (item, equip = true) => {
    let signature = equipmentSignature;
    const message = JSON.stringify({ accountAddress, scope: 'inventory' });

    inventoryItems.filter((it) => it.nftData.type === item.nftData.type).forEach((it) => it.equipped = false);
    item.equipped = equip;
    setInventoryItems(inventoryItems);

    if (item.isReal()) {
      if (!equipmentSignature) {
        signature = await signMessage(wagmiConfig, { message });
        setEquipmentSignature(signature);
      }

      // Update backend equipment in background
      const equippedIds = inventoryItems.filter(i => i.equipped).map(i => i.getId());
      const network = IS_PRODUCTION ? Network.BASE_MAINNET : Network.BASE_SEPOLIA;
      updateEquipment(selectedBroadsider.getId(), equippedIds, signature, message, network);
    }
    setInventoryUpdated(`${new Date().getTime()}`);
  };

  const requestMinting = (amountToMint = 1, ogPasses = []) => {
    writeContract({
      address: BASEJUMP_CONTRACT_ADDRESS,
      abi: BasejumpContractABI,
      functionName: 'multiMint',
      args: [BigInt(amountToMint), ogPasses],
      value: BigInt(priceWei) * (BigInt(amountToMint) + BigInt(ogPasses.length))
    });
  };

  const value = useMemo(
    () => ({
      moonpayProvider,
      moonpaySigner,
      moonpaySdk,
      setMoonpayProvider,
      setMoonpaySdk,
      setMoonpaySigner,
      accountAddress,
      setAccountAddress,
      walletAddress,
      setWalletAddress,
      ownedBroadsiders,
      setOwnedBroadsiders,
      selectedBroadsider,
      setSelectedBroadsider,
      selectBroadsider,
      promoItems,
      setPromoItems,
      metaverseItems,
      setMetaverseItems,
      inventoryItems,
      setInventoryItems,
      inventory,
      setSelectedAsset,
      selectedAsset,
      setTransferAsset,
      transferAsset,
      setQrCodeData,
      qrCodeData,
      setErrorData,
      errorData,
      setShowDesktopOnly,
      showDesktopOnly,
      setShowLogin,
      showLogin,
      setShowVideo,
      showVideo,
      setShowLogout,
      showLogout,
      setVoiceoverApiKey,
      voiceoverApiKey,
      voices,
      setVoices,
      equipItem,
      requestMinting,
      inventoryUpdated,
      showMintModal,
      setShowMintModal,
      mapSrc,
      setMapSrc,
      isMintingInProgress,
      setMintingInProgress,
      mintFigures,
      setMintFigures,
      priceWei,
      setPriceWei,
      completeReceipt,
      setCompleteReceipt,
      isReadLoading,
      readData,
      reloadContract,
      wagmiConfig,
      equipmentSignature,
      setShowUpload,
      showUpload,
      setShowSelector,
      showSelector,
    }),
    [
      selectedBroadsider,
      inventoryUpdated, 
      selectedAsset,
      transferAsset,
      ownedBroadsiders, 
      promoItems, 
      metaverseItems, 
      inventoryItems, 
      inventory,
      qrCodeData, 
      errorData, 
      showLogin, 
      showDesktopOnly, 
      voiceoverApiKey, 
      voices, 
      moonpayProvider, 
      moonpaySigner, 
      accountAddress,
      moonpaySdk, 
      showLogout, 
      showVideo,
      showMintModal,
      mapSrc,
      mintFigures,
      priceWei,
      isReadLoading,
      readData,
      isMintingInProgress,
      completeReceipt,
      wagmiConfig,
      walletAddress,
      showUpload,
      showSelector,
      equipmentSignature
    ]
  );

  return (
    <AppContext.Provider value={value}>{children}</AppContext.Provider>
  );
};
